import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/ui/avatar'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { RecordStatus, RoleScope } from '../types'

export function StatusBadge({ status }: { status: RecordStatus }) {
  const { t } = useTranslation()
  return (
    <Badge tone={status === 'active' ? 'success' : 'neutral'}>
      <span className={cn('size-1.5 rounded-full', status === 'active' ? 'bg-success' : 'bg-muted-foreground')} aria-hidden />
      {t(`access.status.${status}`)}
    </Badge>
  )
}

const SCOPE_TONE: Record<RoleScope, BadgeTone> = {
  organization: 'primary',
  project: 'info',
  department: 'warning',
  own: 'neutral',
}

export function ScopeBadge({ scope }: { scope: RoleScope }) {
  const { t } = useTranslation()
  return <Badge tone={SCOPE_TONE[scope]}>{t(`access.scope.${scope}`)}</Badge>
}

export type AvatarPerson = { id?: string; name: string; src?: string }

export function AvatarStack({ names, people, max = 3 }: { names?: string[]; people?: AvatarPerson[]; max?: number }) {
  const { t } = useTranslation()
  const list: AvatarPerson[] = people ?? (names ?? []).map((name) => ({ name }))
  if (list.length === 0) return <span className="text-sm text-muted-foreground">—</span>
  const rest = list.length - max
  return (
    <div className="flex items-center">
      <div className="flex -space-x-1.5">
        {list.slice(0, max).map((person, index) => (
          <Avatar
            key={person.id ?? `${person.name}-${index}`}
            name={person.name}
            src={person.src}
            className="size-7 ring-2 ring-card [&>span]:text-[10px]"
          />
        ))}
      </div>
      {rest > 0 ? <span className="ml-2 text-xs font-medium text-muted-foreground">{t('access.moreCount', { count: rest })}</span> : null}
    </div>
  )
}
